// Contracts — vista dashboard reads: the team table, the summary tiles, one
// contributor's card and the contributor's own cabinet.
//
// Hours are decimal HOURS on the wire but SECONDS in D1 — the worker converts.
// Fractional hours accumulate rounding error once summed, so seconds stay
// canonical in storage and hours are a presentation concern.
import { z } from 'zod';
import {
  VistaCaptureStatusSchema,
  VistaCentsSchema,
  VistaContributorStatusSchema,
  VistaOrientationSchema,
  VistaTsSchema,
} from './enums';

// GET /api/vista/pool → the caller's contributors, scoped by poolClause.
export const VistaPoolRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  status: VistaContributorStatusSchema,
  uiLang: z.string(),
  // Who currently owns this person. Only meaningful for managers — admins see
  // their own team by definition. The id exists because names collide (two
  // admins can both be «Аня») and the owner filter must not merge their teams.
  ownerId: z.string().nullable(),
  ownerName: z.string().nullable(),
  // What WE pay per accepted hour. null = not set; the UI must not invent a
  // default, that would read as a promise we never made.
  rateCents: VistaCentsSchema.nullable(),
  filmedHours: z.number(),
  deliveredHours: z.number(),
  pendingHours: z.number(),
  acceptedHours: z.number(),
  rejectedHours: z.number(),
  captureCount: z.number().int(),
  // acceptedHours × rateCents — what we owe this person.
  earnedCents: VistaCentsSchema.nullable(),
  // What the CLIENT pays us for their accepted hours. Manager-only money: null
  // for everyone else, since an admin who sees it can derive our margin.
  clientCents: VistaCentsSchema.nullable(),
  lastActiveAt: VistaTsSchema,
});
export type VistaPoolRow = z.infer<typeof VistaPoolRowSchema>;

export const VistaPoolResSchema = z.object({
  rows: z.array(VistaPoolRowSchema),
  count: z.number().int(),
});
export type VistaPoolRes = z.infer<typeof VistaPoolResSchema>;

// A work queue: hours + files sitting in one stage of the pipeline.
export const VistaQueueSchema = z.object({
  hours: z.number(),
  files: z.number().int(),
});

// GET /api/vista/stats/summary → tiles + the queues worth acting on.
export const VistaSummaryResSchema = z.object({
  people: z.number().int(),
  active: z.number().int(),
  filmedHours: z.number(),
  deliveredHours: z.number(),
  pendingHours: z.number(),
  acceptedHours: z.number(),
  rejectedHours: z.number(),
  // null when the caller may not see client money — distinct from 0, which
  // would read as "nothing owed". Summed PER COUNTRY: the client pays different
  // rates by geography, so there is no single multiplication to do.
  clientCents: VistaCentsSchema.nullable(),
  // Accepted hours in countries with no rate on file. They are EXCLUDED from
  // clientCents, so the UI can say "plus N hours not priced yet" instead of
  // showing a total that silently misses a country.
  unpricedHours: z.number(),
  // Material filmed but not yet dubbed, and dubbed but not yet shipped. Nothing
  // expires on a timer here, but either queue standing still is unpaid work.
  toDub: VistaQueueSchema,
  toShip: VistaQueueSchema,
});
export type VistaSummaryRes = z.infer<typeof VistaSummaryResSchema>;

// GET /api/vista/contributor/:id → card.
export const VistaContributorPersonSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  phone: z.string().nullable(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  status: VistaContributorStatusSchema,
  uiLang: z.string(),
  note: z.string().nullable(),
  // F3 — почта штабного аккаунта, к которому привязан этот съёмочный кабинет
  // (migration 0005). null = не привязан ни к кому: тогда переключатель
  // кабинетов человеку не покажется, даже если он же сидит в штабе.
  linkedUserEmail: z.string().nullable(),
  rateCents: VistaCentsSchema.nullable(),
  filmedHours: z.number(),
  deliveredHours: z.number(),
  pendingHours: z.number(),
  acceptedHours: z.number(),
  rejectedHours: z.number(),
  captureCount: z.number().int(),
  earnedCents: VistaCentsSchema.nullable(),
  clientCents: VistaCentsSchema.nullable(),
  lastActiveAt: VistaTsSchema,
});

export const VistaDailyRowSchema = z.object({
  // UTC YYYY-MM-DD, bucketed by RECORDED time: bucketing by upload moves an
  // evening shift into the next day for anyone who uploads over night Wi-Fi.
  day: z.string(),
  filmedHours: z.number(),
  deliveredHours: z.number(),
  pendingHours: z.number(),
  acceptedHours: z.number(),
  rejectedHours: z.number(),
  captureCount: z.number().int(),
});

// One clip as the cabinets list it.
export const VistaCaptureRowSchema = z.object({
  id: z.string(),
  fileName: z.string().nullable(),
  taskSlug: z.string().nullable(),
  durationSeconds: z.number().int().nullable(),
  status: VistaCaptureStatusSchema,
  rejectReason: z.string().nullable(),
  orientation: VistaOrientationSchema.nullable(),
  recordedAt: VistaTsSchema,
  uploadedAt: VistaTsSchema,
  // Optional: only /my/stats fills it in (to gate the "watch" button — a
  // rejected clip with bytes=0 was discarded before any footage landed, see
  // handleCaptureAbort). /contributor/:id does not select it and stays
  // exactly as it was — an absent field parses fine through `.optional()`.
  bytes: z.number().int().optional(),
});
export type VistaCaptureRow = z.infer<typeof VistaCaptureRowSchema>;

// The staff card adds the two fields a contributor has no use for: how close the
// recording came to its own timer, and when it went out to the client.
export const VistaStaffCaptureRowSchema = VistaCaptureRowSchema.extend({
  integrityRatio: z.number().nullable(),
  deliveredAt: VistaTsSchema,
});

// Ownership history — why assignments is a table and not a column: reassigning
// a person must not rewrite who earned on their past hours.
export const VistaOwnershipSchema = z.object({
  ownerName: z.string().nullable(),
  fromAt: z.number().int(),
  toAt: z.number().int().nullable(),
});

// GET /api/vista/my/stats — the contributor's own cabinet. A deliberate subset
// of the staff card: their hours, THEIR earnings at THEIR rate, their clips with
// reject reasons. No owner history, no client money — none of that is theirs.
export const VistaMyStatsResSchema = z.object({
  me: z.object({
    email: z.string(),
    name: z.string().nullable(),
    status: VistaContributorStatusSchema,
    rateCents: VistaCentsSchema.nullable(),
    filmedHours: z.number(),
    deliveredHours: z.number(),
    pendingHours: z.number(),
    acceptedHours: z.number(),
    rejectedHours: z.number(),
    captureCount: z.number().int(),
    // acceptedHours × rateCents, null while no rate is set.
    earnedCents: VistaCentsSchema.nullable(),
    lastActiveAt: VistaTsSchema,
  }),
  daily: z.array(VistaDailyRowSchema),
  recent: z.array(VistaCaptureRowSchema),
});
export type VistaMyStatsRes = z.infer<typeof VistaMyStatsResSchema>;

/** Строка журнала действий. Нужна ровно для одного разговора: «кто это
 *  сделал». Без неё спор о ставке или вердикте упирается в «я не менял». */
export const VistaActivityRowSchema = z.object({
  event: z.string(),
  detail: z.string().nullable(),
  actorName: z.string().nullable(),
  createdAt: z.number().int(),
});

export const VistaContributorResSchema = z.object({
  person: VistaContributorPersonSchema,
  activity: z.array(VistaActivityRowSchema).default([]),
  daily: z.array(VistaDailyRowSchema),
  recent: z.array(VistaStaffCaptureRowSchema),
  ownership: z.array(VistaOwnershipSchema),
});
export type VistaContributorRes = z.infer<typeof VistaContributorResSchema>;

// ── F8: plan-to-deadline / pace ─────────────────────────────────────────────
// The goal is counted in HOURS OF ACCEPTED MATERIAL (vista pays per accepted
// hour; kids' session-count model does not apply here). It is a SINGLE
// company-wide number, not per-pool: manager and admin see the identical
// figures — "одно правило всем ролям" (Tim, 2026-08-11). That is also why
// GET /pace is not run through poolClause.

// GET /api/vista/pace — admin + manager. targetHours/deadline are null
// together whenever no goal has been set yet (they are always written as a
// pair by PUT .../pace/goal); the rest is computed live off the same numbers
// the pool screen already shows.
export const VistaPaceResSchema = z.object({
  targetHours: z.number().nullable(),
  deadline: z.string().nullable(), // YYYY-MM-DD
  acceptedHours: z.number(),
  // null only when no goal is set — distinct from 0, which would mean "due today".
  daysRemaining: z.number().int().nullable(),
  // (target − accepted) / daysRemaining, rounded once. When the deadline has
  // already passed, this is simply the remaining hours (all of it is "due now"),
  // not a division by zero.
  requiredPerDay: z.number().nullable(),
  // Accepted hours over the last 7 days, ACROSS THE WHOLE POOL, divided by 7 —
  // the standing "are we keeping pace" number, always computable even with no
  // goal set.
  recentPerDay: z.number(),
  // null when no goal is set. true when the goal is already met OR the last-7d
  // pace already clears requiredPerDay.
  onTrack: z.boolean().nullable(),
});
export type VistaPaceRes = z.infer<typeof VistaPaceResSchema>;

// PUT /api/vista/pace/goal — manager only. Both fields together: a target
// with no deadline (or vice versa) can't drive "hours needed per day", so the
// pair is set and cleared as one unit.
export const VistaSetPaceGoalReqSchema = z.object({
  targetHours: z.number().positive().max(1_000_000),
  deadline: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'deadline must be YYYY-MM-DD'),
});
export type VistaSetPaceGoalReq = z.infer<typeof VistaSetPaceGoalReqSchema>;

export const VistaSetPaceGoalResSchema = z.object({ ok: z.literal(true) });
export type VistaSetPaceGoalRes = z.infer<typeof VistaSetPaceGoalResSchema>;

// ── A3: "кто пишет прямо сейчас" — live dashboard ───────────────────────────
// admin + manager only (same bar as /pace), scoped through poolClause like
// every other pool read — an admin sees only their own team's live rows.

// One row currently streaming bytes: `captures.status='recording'` AND its
// `uploads` row was touched in the last 2 minutes (a part landed recently).
// Anything older than that is not "recording right now" — it is abandoned,
// and salvageAbandoned() (the hourly cron) will eventually close it out; this
// screen must not call a stalled upload "live".
export const VistaLiveRecordingSchema = z.object({
  contributorId: z.string(),
  name: z.string().nullable(),
  email: z.string(),
  taskSlug: z.string().nullable(),
  // capture.recorded_at (client clock at filming start, falls back to the
  // server's capture-open time) — the UI counts up from this, not from a
  // server-computed duration, so the clock keeps ticking between polls.
  startedAt: z.number().int(),
  bytesReceived: z.number().int(),
});
export type VistaLiveRecording = z.infer<typeof VistaLiveRecordingSchema>;

// GET /api/vista/live-now → who is filming right now, plus today's shape.
export const VistaLiveNowResSchema = z.object({
  recording: z.array(VistaLiveRecordingSchema),
  // Today = UTC day, same bucketing as contributor_daily. Sourced from that
  // rollup, not a live scan of captures, so this stays cheap as history grows.
  today: z.object({
    clips: z.number().int(),
    hours: z.number(),
    acceptedHours: z.number(),
    pendingHours: z.number(),
  }),
  // Distinct contributors with at least one clip touched today, in-scope.
  activeToday: z.number().int(),
});
export type VistaLiveNowRes = z.infer<typeof VistaLiveNowResSchema>;
